import { FDate, FIDate, Icon, IconLib, Popover, sameDay, stopEvent, Text, useVisibility } from '@fold-dev/core'
import { useMemo } from 'react'
import { getShortDateFormat } from '../../helpers'
import { CalendarTypes } from '../calendar'
import { ActionButton } from './action-button'
import { DateSelect } from './date-select'
import { TbRotate2 } from 'react-icons/tb'

export const getStartEndRepeat = (start, end, repeat) => {
    return {
        start: start ? new Date(start) : undefined,
        end: end ? new Date(end) : undefined,
        repeat: repeat
            ? {
                  ...repeat,
                  from: new Date(repeat.from),
                  to: repeat.to ? new Date(repeat.to) : undefined,
              }
            : undefined,
    }
}

function formatDateText(date) {
    const now = new Date()
    const today: any = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const inputDate: any = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    const diffDays = (inputDate - today) / (1000 * 60 * 60 * 24)

    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Tomorrow'
    if (diffDays === -1) return 'Yesterday'
    if (diffDays > 1 && diffDays < 7) {
        return date.toLocaleDateString('en-US', { weekday: 'long' })
    }
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })
}

export const useDateButton = (start, end, repeat, wholeDay, complete) => {
    const { dateLabel, dateColor, dateBackgroundColor, dateIcon } = useMemo(() => {
        const dateIcon = !end && !!repeat ? TbRotate2 : FIDate
        const hasDate = start || end
        let hasOneDate = (!start && end) || (start && !end)
        let dateColor, dateLabel, dateBackgroundColor

        if (hasDate) {
            if (!hasOneDate) {
                if (sameDay(start, end)) hasOneDate = true
            }

            if (hasOneDate) {
                const date = start || end
                const isPast = new Date().getTime() - date.getTime() > 0

                if (FDate(date).isToday()) {
                    dateLabel = 'Today'
                    dateColor = 'var(--f-color-success)'
                    dateBackgroundColor = `rgb(from var(--f-color-success) r g b / 12.5%)`
                } else if (FDate(date).isTomorrow()) {
                    dateLabel = 'Tomorrow'
                } else if (FDate(date).isYesterday()) {
                    dateLabel = 'Yesterday'
                    dateColor = 'var(--f-color-warning)'
                    dateBackgroundColor = `rgb(from var(--f-color-warning) r g b / 12.5%)`
                } else if (isPast) {
                    dateLabel = FDate(date).fromNow()
                    dateColor = 'var(--f-color-danger)'
                    dateBackgroundColor = `rgb(from var(--f-color-danger) r g b / 12.5%)`
                } else {
                    dateLabel = formatDateText(date) //FDate(date).fromNow()
                }
            } else {
                const isPast = new Date().getTime() - end.getTime() > 0

                if (FDate(end).isToday()) {
                    dateLabel = 'Today'
                    dateColor = 'var(--f-color-success)'
                    dateBackgroundColor = `rgb(from var(--f-color-success) r g b / 12.5%)`
                } else if (FDate(end).isTomorrow()) {
                    dateLabel = 'Tomorrow'
                } else if (FDate(end).isYesterday()) {
                    dateLabel = 'Yesterday'
                    dateColor = 'var(--f-color-warning)'
                    dateBackgroundColor = `rgb(from var(--f-color-warning) r g b / 12.5%)`
                } else if (isPast) {
                    dateLabel = FDate(end).fromNow()
                    dateColor = 'var(--f-color-danger)'
                    dateBackgroundColor = `rgb(from var(--f-color-danger) r g b / 12.5%)`
                } else {
                    dateLabel = FDate(end).fromNow()
                }

                dateLabel = `${getShortDateFormat(start)} - ${getShortDateFormat(end)}`
            }
        }

        if (complete) dateColor = undefined
        if (complete) dateBackgroundColor = undefined

        return {
            dateLabel,
            dateColor,
            dateBackgroundColor,
            dateIcon,
        }
    }, [start, end, wholeDay, repeat])

    return {
        dateLabel,
        dateColor,
        dateBackgroundColor,
        dateIcon,
    }
}

export type DateButtonProps = {
    kanbanMode?: boolean
    complete?: boolean
    start: Date
    end: Date
    repeat?: CalendarTypes.Repeat
    wholeDay: boolean
    onDateChange: ({ start, end, repeat, wholeDay }) => void
    onDelete: () => void
    portal?: any
    buttonStyleProps?: any
}

export const DateButton = (props: DateButtonProps) => {
    const { complete, onDateChange, onDelete, portal, wholeDay, kanbanMode, buttonStyleProps = {} } = props
    const { visible, show, hide } = useVisibility(false)
    const { start, end, repeat } = useMemo(() => {
        return getStartEndRepeat(props.start, props.end, props.repeat)
    }, [props.start, props.end, props.repeat])
    const { dateLabel, dateColor, dateBackgroundColor, dateIcon } = useDateButton(
        start ? new Date(start) : undefined,
        end ? new Date(end) : undefined,
        repeat,
        wholeDay,
        complete
    )

    const renderLabel = () => {
        return (
            <div
                onClick={(e) => {
                    stopEvent(e)
                    show()
                }}
                className="f-text sm"
                style={{ color: dateColor ? dateColor : 'var(--f-color-text-weaker)' }}>
                {dateLabel}
            </div>
        )
    }

    return (
        <Popover
            portal={portal}
            focusTrap={false}
            width="fit-content"
            content={
                <div
                    onMouseDown={stopEvent}
                    onClick={stopEvent}>
                    <DateSelect
                        start={start}
                        end={end}
                        repeat={repeat}
                        wholeDay={wholeDay}
                        onCancel={hide}
                        onSave={({ start, end, repeat, wholeDay }) => {
                            onDateChange({ start, end, repeat, wholeDay })
                            hide()
                        }}
                    />
                </div>
            }
            isVisible={visible}
            onDismiss={hide}>
            <div
                onMouseDown={stopEvent}
                className="f-row action-button-parent"
                style={{
                    color: dateColor,
                    gap: 5,
                    ...buttonStyleProps,
                }}>
                {dateLabel && !kanbanMode && renderLabel()}
                <ActionButton
                    className={!dateLabel ? (visible ? 'hide-is-open' : 'hide') : undefined}
                    style={{
                        padding: dateLabel ? '0 0.25rem 0 4px' : 0,
                        borderWidth: dateColor ? 1 : undefined,
                        borderStyle: dateLabel ? 'solid' : 'dashed',
                        borderColor: dateColor ? dateColor : undefined,
                        color: dateColor ? dateColor : undefined,
                    }}
                    border
                    onClick={show}>
                    <Icon
                        icon={dateIcon}
                        size="sm"
                    />
                </ActionButton>
                {dateLabel && kanbanMode && renderLabel()}
            </div>
        </Popover>
    )
}
